/* eslint-disable @next/next/no-img-element */
'use client'

import { aspectRatioToPaddingBottom, DEFAULT_SLIDER_ASPECT_RATIO, type TileAspectRatio } from '@/components/scenario/aspectRatio'
import { DEFAULT_THUMBNAIL_ROUNDED, THUMBNAIL_RADIUS_SP, thumbnailRadius } from '@/components/scenario/thumbnailCorner'
import { createThumbnailHoverHandlers } from '@/components/video/HoverAnimatedThumbnail'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import UnifiedPlayMark from '@/components/embedding/UnifiedPlayMark'
import React, { FC, RefObject, useCallback } from 'react'

export interface SPSliderCarouselProps {
  orderedNodes: ScenarioNodeWithVideoUrl[]
  spScrollRef: RefObject<HTMLDivElement | null>
  onThumbnailClick: (absoluteIndex: number) => void
  aspectRatio?: TileAspectRatio
  /** サムネイルの角丸。false なら直角（先方サイトのデザインに合わせる設定） */
  thumbnailRounded?: boolean
}

const arrowStyle: React.CSSProperties = { background: 'white', color: '#9ca3af', border: '1px solid rgb(156, 163, 175)', width: '42px', height: '42px' }
const arrowHoverIn = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.borderColor = '#6b7280'; e.currentTarget.style.color = '#4b5563' }
const arrowHoverOut = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.borderColor = '#9ca3af'; e.currentTarget.style.color = '#9ca3af' }

const SPSliderCarousel: FC<SPSliderCarouselProps> = ({
  orderedNodes,
  spScrollRef,
  onThumbnailClick,
  aspectRatio = DEFAULT_SLIDER_ASPECT_RATIO,
  thumbnailRounded = DEFAULT_THUMBNAIL_ROUNDED,
}) => {
  const cornerRadius = thumbnailRadius(thumbnailRounded, THUMBNAIL_RADIUS_SP)
  const scrollByCard = useCallback((direction: 'prev' | 'next') => {
    const container = spScrollRef.current
    /* v8 ignore next */
    if (!container) return
    const cardWidth = container.offsetWidth * 0.65 + 12 // 65% + gap
    container.scrollBy({ left: direction === 'next' ? cardWidth : -cardWidth, behavior: 'smooth' })
  }, [spScrollRef])

  return (
    <>
      <style>{`
        /* A7: SP Carousel styles */
        .sp-slider-scroll {
          display: flex;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
          gap: 12px;
          padding: 0;
        }

        .sp-slider-scroll::-webkit-scrollbar {
          display: none;
        }

        .sp-slider-card {
          flex: 0 0 65%;
          scroll-snap-align: center;
          cursor: pointer;
          border-radius: ${cornerRadius};
          overflow: hidden;
          background: #f3f4f6;
          transition: transform 200ms ease, box-shadow 200ms ease;
        }

        .sp-slider-card:active {
          transform: scale(0.98);
        }

        .sp-slider-card-inner {
          border-radius: ${cornerRadius};
        }

        .sp-slider-card .play-overlay {
          opacity: 1 !important;
          background: transparent;
        }
        .sp-slider-card:hover .play-overlay {
          background: rgba(0, 0, 0, 0.3);
        }
      `}</style>

      <div className="relative flex items-center gap-1">
        {/* 左矢印 */}
        {orderedNodes.length > 1 && (
          <button
            type="button"
            className="flex-shrink-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 z-10" style={arrowStyle} onMouseEnter={arrowHoverIn} onMouseLeave={arrowHoverOut}
            onClick={() => scrollByCard('prev')}
            aria-label="前へ"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          </button>
        )}

        <div ref={spScrollRef} className="sp-slider-scroll flex-1">
          {orderedNodes.map((node, index) => (
            <div
              key={node.id}
              className="sp-slider-card group"
              onClick={() => onThumbnailClick(index)}
            >
              <div className="sp-slider-card-inner relative w-full overflow-hidden bg-black" style={{ paddingBottom: aspectRatioToPaddingBottom(aspectRatio) }}>
                {node.thumbnailUrl ? (
                  <img
                    src={node.thumbnailUrl}
                    alt={`動画 ${index + 1}`}
                    className="absolute top-0 left-0 w-full h-full object-cover"
                    {...createThumbnailHoverHandlers(node.thumbnailUrl, node.animatedThumbnailUrl)}
                  />
                ) : (
                  <div className="absolute top-0 left-0 w-full h-full bg-gray-500" />
                )}
                <div className="play-overlay pointer-events-none absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-0 transition-all duration-200">
                  <UnifiedPlayMark />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 右矢印 */}
        {orderedNodes.length > 1 && (
          <button
            type="button"
            className="flex-shrink-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 z-10" style={arrowStyle} onMouseEnter={arrowHoverIn} onMouseLeave={arrowHoverOut}
            onClick={() => scrollByCard('next')}
            aria-label="次へ"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </button>
        )}
      </div>
    </>
  )
}

export default SPSliderCarousel
